import { z } from 'zod'
import { createApi } from '@reduxjs/toolkit/query/react'

import { decodeAndGet } from '../../utils'

import type { User } from '../auth'
import type { Market } from '../market'
import type { OutletDescription, Orders, CornerStatus, Order } from '../types'
import { ordersDecoder, orderDecoder, outletDecoder } from '../decoders'
import { baseQueryWithAuth } from './baseQuery'

// --- DECODERS --- //

const authDecoder = z.object({ access_token: z.string() })

const userDecoder = z.object({
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  email: z.string().nullable(),
  username: z.string(),
  id: z.number(),
  roles: z.array(z.string()),
})

const marketsDecoder = z.object({
  id: z.number(),
  title: z.string(),
  status: z.number(),
  is_for_yandex: z.number(),
})

// --- API --- //
// RTK 1.6.2 has type issues with providesTags/invalidatesTags generics.
// Using `as any` on tag arrays to work around this — the runtime behavior is correct.

export const api = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['User', 'Markets', 'Outlets', 'Orders', 'Order'] as never,
  endpoints: (build) => ({
    // Auth endpoints
    loginByPassword: build.mutation<{ accessToken: string }, { username: string; password: string }>({
      query: (body) => ({
        url: '/auth/login_by_username',
        method: 'POST',
        body,
      }),
      transformResponse: (response: unknown) => {
        const [data, error] = decodeAndGet(authDecoder, response)
        if (error) throw new Error(error)
        return { accessToken: data!.access_token }
      },
    }),
    getMe: build.query<User, void>({
      query: () => '/users/me',
      transformResponse: (response: unknown) => {
        const [data, error] = decodeAndGet(userDecoder, response)
        if (error) throw new Error(error)
        const { id, ...rest } = data!
        return { ...rest, code: id } as User
      },
      providesTags: ['User'] as never,
    }),
    registerPushToken: build.mutation<void, { token: string; platform: string }>({
      query: (body) => ({
        url: '/fcm/register',
        method: 'POST',
        body: {
          ...body,
          type: 'Web',
          clientApp: 'ARM',
        },
      }),
    }),
    revokePushToken: build.mutation<void, { token: string }>({
      query: (body) => ({
        url: '/fcm/revoke',
        method: 'POST',
        body,
      }),
    }),

    // Market endpoints
    getMarkets: build.query<Market[], void>({
      query: () => '/arm/runner/markets',
      transformResponse: (response: unknown) => {
        const [data, error] = decodeAndGet(z.array(marketsDecoder), response)
        if (error) throw new Error(error)
        return data!
      },
      providesTags: ['Markets'] as never,
    }),
    getOutlets: build.query<OutletDescription[], number>({
      query: (marketId) => `/arm/markets/outlets/${marketId}`,
      transformResponse: (response: unknown) => {
        const [data, error] = decodeAndGet(z.array(outletDecoder), response)
        if (error) throw new Error(error)
        return data! as OutletDescription[]
      },
      providesTags: ['Outlets'] as never,
    }),
    changeUserMarket: build.mutation<void, { courierMarketId: number }>({
      query: (body) => ({
        url: '/arm/runner/market_change',
        method: 'POST',
        body: {
          market_id: body.courierMarketId,
        },
      }),
    }),

    // Orders endpoints
    getOrders: build.query<Orders, number>({
      query: (marketId) => ({
        url: '/arm/runner/orders',
        method: 'POST',
        body: { market_id: marketId },
      }),
      transformResponse: (response: unknown) => {
        const [data, error] = decodeAndGet(ordersDecoder, response)
        if (error) throw new Error(error)
        return data! as Orders
      },
      providesTags: ['Orders'] as never,
    }),

    // Order detail endpoints
    getOrder: build.query<Order, number>({
      query: (id) => `/arm/runner/order/${id}`,
      transformResponse: (response: unknown) => {
        const [data, error] = decodeAndGet(orderDecoder, response)
        if (error) throw new Error(error)
        return data! as Order
      },
      providesTags: (_result, _error, id) => [{ type: 'Order' as const, id }] as never,
    }),
    updateCornerStatus: build.mutation<void, { orderId: number; outletOrderId: number; status: CornerStatus }>({
      query: ({ outletOrderId, status }) => ({
        url: `/arm/order/${outletOrderId}/status/${status}`,
        method: 'GET',
      }),
      onQueryStarted: async ({ orderId, outletOrderId, status }, { dispatch, queryFulfilled }) => {
        const patchResult = dispatch(
          api.util.updateQueryData('getOrder', orderId, (draft: any) => {
            const outlet = draft?.order_outlets.find((o: any) => o.id === outletOrderId)
            if (outlet) {
              outlet.status = status
            }
          }),
        )
        try {
          await queryFulfilled
        } catch {
          patchResult.undo()
        }
      },
      invalidatesTags: (_result, _error, { orderId }) => [
        { type: 'Order' as const, id: orderId },
        'Orders',
      ] as never,
    }),
    updateOrderStatus: build.mutation<void, { orderId: number }>({
      query: ({ orderId }) => ({
        url: `/arm/order/${orderId}/full/status/received`,
        method: 'POST',
        body: {},
      }),
      invalidatesTags: (_result, _error, { orderId }) => [{ type: 'Order' as const, id: orderId }, 'Orders'] as never,
    }),
    checkYandexCode: build.mutation<{ isValid: boolean; matchedBy: string }, { orderId: number; code: string }>({
      query: ({ orderId, code }) => ({
        url: `/arm/order/${orderId}/check_yandex_code`,
        method: 'POST',
        body: { code },
      }),
    }),
    robotOpenLid: build.mutation<void, { orderId: number }>({
      query: ({ orderId }) => ({
        url: '/yandex_delivery/robot/open',
        method: 'POST',
        body: { order_id: orderId },
      }),
      invalidatesTags: (_result, _error, { orderId }) => [{ type: 'Order' as const, id: orderId }, 'Orders'] as never,
    }),
    robotCloseLid: build.mutation<void, { orderId: number }>({
      query: ({ orderId }) => ({
        url: '/yandex_delivery/robot/close',
        method: 'POST',
        body: { order_id: orderId },
      }),
      invalidatesTags: (_result, _error, { orderId }) => [{ type: 'Order' as const, id: orderId }, 'Orders'] as never,
    }),
    /** Открыть крышку робота Яндекс Еды (Partner API OpenRobotHatch). */
    openRobotHatch: build.mutation<{ ok: boolean }, { orderId: number }>({
      query: ({ orderId }) => ({
        url: `/yandex/order/${orderId}/open-robot-hatch`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { orderId }) => [{ type: 'Order' as const, id: orderId }, 'Orders'] as never,
    }),
  }),
})

// Export hooks for all endpoints
export const {
  // Auth
  useLoginByPasswordMutation,
  useGetMeQuery,
  useRegisterPushTokenMutation,
  useRevokePushTokenMutation,
  // Market
  useGetMarketsQuery,
  useGetOutletsQuery,
  useChangeUserMarketMutation,
  // Orders
  useGetOrdersQuery,
  // Order detail
  useGetOrderQuery,
  useUpdateCornerStatusMutation,
  useUpdateOrderStatusMutation,
  useCheckYandexCodeMutation,
  useRobotOpenLidMutation,
  useRobotCloseLidMutation,
  useOpenRobotHatchMutation,
} = api
